/**
 * Render-side life for the creatures in the Merge jar: how each body is drawn this frame on top
 * of where physics put it. Nothing here touches physics; it only reads positions, velocities
 * and the merge / shrink tweens, and returns a drawn transform:
 *
 *   - landing squash: a body that suddenly loses speed along its heading squashes against
 *     what it hit and wobbles back (`sy = 1 − A·e^(−10t)·cos 26t`), anchored at the contact so
 *     it stays on the floor; falling bodies stretch a little along their velocity;
 *   - idle breathing: a slow anti-phase wobble per body (keeps volume, anchored at the bottom);
 *   - blinking: an occasional blink per body (and eyes squeezed on hard hits and while merging);
 *   - merging: the pair stretches toward each other, squashes wide (anticipation), then gulps
 *     inward and tall, ending near the size the new creature pops in from (juice getMergePop);
 *   - merge pop-in: the new creature overshoots with a squash-stretch wobble;
 *   - shrink: the shrunk replacement body boings.
 *
 * State is keyed by Matter body id. Ids change when a merge or a shrink makes a new body and
 * when a resize rebuilds them all; ids are never reused, so a new id just starts fresh (with
 * the shrink handover matched by position). Entries not seen for a frame are dropped.
 *
 * Every transform is a 2×2 matrix `m` [a, b, c, d] (x' = a·x + c·y, y' = b·x + d·y) applied in
 * world space about the body centre after the sprite is sized and rotated, plus a centre shift.
 */
import { hash } from "../toon-kit.js";

var TAU = Math.PI * 2;

/** Tuning in units of the tier-0 radius (velocities in Matter units, panel px per step). */
export var BODY_LIFE = {
  /** Heading speed (÷ r0) a body must have had for a slow-down to count as an impact. */
  impactMinSpeed: 0.12,
  /** Speed lost along the heading (÷ r0) where the squash starts, and where it is full. */
  impactStart: 0.1,
  impactFull: 0.45,
  /** Peak squash (share of the radius) for a full impact on a tier-0 body. */
  squash: 0.27,
  squashDecay: 10,
  squashFreq: 26,
  /** Velocity stretch: starts at this speed (÷ r0), gains per unit, capped. */
  stretchFrom: 0.2,
  stretchGain: 0.22,
  stretchMax: 0.1,
  /** Idle breathing (share of the radius); small so resting neighbours stay touching. */
  breathe: 0.011,
  /** Seconds a blink keeps the eyes shut. */
  blinkShut: 0.12,
};

function clamp(v, a, b) {
  return v < a ? a : v > b ? b : v;
}

function smoothstep(a, b, x) {
  var t = clamp((x - a) / (b - a), 0, 1);
  return t * t * (3 - 2 * t);
}

/** m1 · m2 for column-major [a, b, c, d]. */
function mul(m1, m2) {
  return [
    m1[0] * m2[0] + m1[2] * m2[1],
    m1[1] * m2[0] + m1[3] * m2[1],
    m1[0] * m2[2] + m1[2] * m2[3],
    m1[1] * m2[2] + m1[3] * m2[3],
  ];
}

/**
 * Scale by `k` along the unit axis (ux, uy) and by 1/k across it (volume kept).
 * @returns {number[]}
 */
export function axisSquash(k, ux, uy) {
  var inv = 1 / k;
  var cc = ux * ux, ss = uy * uy, cs = ux * uy;
  var off = (k - inv) * cs;
  return [k * cc + inv * ss, off, off, k * ss + inv * cc];
}

var IDENTITY = [1, 0, 0, 1];

function isIdentity(m) {
  return Math.abs(m[0] - 1) < 1e-4 && Math.abs(m[1]) < 1e-4 && Math.abs(m[2]) < 1e-4 && Math.abs(m[3] - 1) < 1e-4;
}

/**
 * Where a merging body is in the physics tween (0–1), read back from what physics wrote so a
 * paused game holds still: positions during the attract part (t < 0.42), the grow scale after.
 */
export function mergeProgress(anim, body, motion) {
  var scale = motion && motion.scale != null && isFinite(motion.scale) ? motion.scale : 1;
  if (scale > 1.0005) return clamp(0.42 + 0.58 * (scale - 1) / 0.35, 0.42, 1);
  if (!anim || !body) return 0;
  var isA = anim.a === body || (anim.a && anim.a.id === body.id);
  var sx = isA ? anim.startAx : anim.startBx;
  var sy = isA ? anim.startAy : anim.startBy;
  var d0 = Math.sqrt((anim.mx - sx) * (anim.mx - sx) + (anim.my - sy) * (anim.my - sy));
  if (!(d0 > 0.5)) return 0.42;
  var px = body.position.x, py = body.position.y;
  var d = Math.sqrt((anim.mx - px) * (anim.mx - px) + (anim.my - py) * (anim.my - py));
  return 0.42 * clamp(1 - d / d0, 0, 1);
}

/**
 * Drawn transform of a merging body at tween progress `t`.
 * @returns {{ m: number[], alpha: number, blink: boolean }}
 */
export function mergePose(t, dirX, dirY) {
  if (t < 0.42) {
    // Attract: stretch toward the partner, most in the middle of the slide.
    var k = t / 0.42;
    var st = 1 + 0.24 * Math.sin(Math.PI * Math.min(1, k * 1.1));
    var len = Math.sqrt(dirX * dirX + dirY * dirY);
    if (!(len > 1e-6)) return { m: IDENTITY, alpha: 1, blink: false };
    return { m: axisSquash(st, dirX / len, dirY / len), alpha: 1, blink: k > 0.7 };
  }
  // Gulp: squash wide (anticipation), then pull in small and tall, handing over to the pop-in.
  var u = (t - 0.42) / 0.58;
  var w1 = smoothstep(0, 0.45, u);
  var w2 = smoothstep(0.45, 1, u);
  var kv = 1 - 0.28 * w1 + 0.5 * w2;
  var s = 1 + 0.12 * w1 - 0.5 * w2;
  var m = axisSquash(kv, 0, 1);
  return {
    m: [m[0] * s, m[1] * s, m[2] * s, m[3] * s],
    alpha: 1 - smoothstep(0.82, 1, u),
    blink: true,
  };
}

/**
 * Pop-in of a creature a merge just made: `p` from the juice (0.55 → ≈1.13 → 1). It starts tall
 * (continuing the gulp) and squashes wide as it overshoots.
 */
export function popPose(p) {
  var kv = clamp(1 - (p - 1) * 0.8, 0.7, 1.45);
  var m = axisSquash(kv, 0, 1);
  return [m[0] * p, m[1] * p, m[2] * p, m[3] * p];
}

/** Landing squash factor along the impact axis, `age` seconds after a hit of amplitude `amp`. */
export function landingSquash(amp, age) {
  if (!(amp > 0)) return 1;
  return 1 - amp * Math.exp(-age * BODY_LIFE.squashDecay) * Math.cos(age * BODY_LIFE.squashFreq);
}

/** Whether a body with this id blinks at time `t` (seconds): a short blink every few seconds. */
export function isBlinking(id, t) {
  var period = 2.6 + 3.4 * hash(id * 1.37 + 0.5);
  var phase = hash(id * 2.71 + 3.1) * period;
  var k = (t + phase) % period;
  if (k < BODY_LIFE.blinkShut) return true;
  // Some creatures double-blink.
  return hash(id * 0.91 + 7.3) > 0.62 && k > 0.24 && k < 0.24 + BODY_LIFE.blinkShut;
}

/**
 * @typedef {Object} BodyPose
 * @property {number[]} m world-space deform [a, b, c, d] about the centre
 * @property {number} dx centre shift (px)
 * @property {number} dy
 * @property {number} alpha
 * @property {boolean} blink eyes shut this frame
 * @property {boolean} lift draw above neighbours (merging / popping)
 * @property {boolean} identity no deform and no shift
 */

/**
 * One life tracker per jar. `begin(t, r0)` each frame (t: pause-aware seconds), `pose(...)`
 * per body, `end()` after the loop.
 */
export function createBodyLife() {
  var states = new Map();
  var frameT = null;
  var dt = 0;
  var r0 = 20;
  var stamp = 0;
  // Shrinking bodies' last centres, so the replacement body (a new id) can boing.
  var shrinkSpots = [];

  function stateFor(id, body) {
    var st = states.get(id);
    if (!st) {
      st = {
        pvx: body.velocity ? body.velocity.x : 0,
        pvy: body.velocity ? body.velocity.y : 0,
        hitAmp: 0,
        hitAge: 9,
        nx: 0,
        ny: 1,
        stretch: 0,
        sax: 0,
        say: 1,
        rest: 1,
        seen: stamp,
        fresh: true,
      };
      states.set(id, st);
    }
    return st;
  }

  function trackImpact(st, body, r) {
    var v = body.velocity || { x: 0, y: 0 };
    if (dt > 0 && !body.isStatic) {
      var ps = Math.sqrt(st.pvx * st.pvx + st.pvy * st.pvy);
      if (ps > BODY_LIFE.impactMinSpeed * r0) {
        // Speed lost along the previous heading; gravity only ever adds to it.
        var lost = -((v.x - st.pvx) * st.pvx + (v.y - st.pvy) * st.pvy) / ps / r0;
        if (lost > BODY_LIFE.impactStart) {
          var strength = clamp((lost - BODY_LIFE.impactStart) / (BODY_LIFE.impactFull - BODY_LIFE.impactStart), 0, 1);
          // Big creatures are heavy: they squash a little less.
          var heavy = clamp((r / r0 - 1) / 5, 0, 1);
          var amp = BODY_LIFE.squash * (0.35 + 0.65 * strength) * (1 - 0.35 * heavy);
          var current = st.hitAmp * Math.exp(-st.hitAge * BODY_LIFE.squashDecay);
          if (amp > current) {
            st.hitAmp = amp;
            st.hitAge = 0;
            st.nx = st.pvx / ps;
            st.ny = st.pvy / ps;
          }
        }
      }
    }
    st.pvx = v.x;
    st.pvy = v.y;
  }

  return {
    /**
     * @param {number} t pause-aware seconds
     * @param {number} unitR tier-0 radius (px): velocity thresholds scale with the jar
     */
    begin: function (t, unitR) {
      var now = isFinite(t) ? t : 0;
      dt = frameT == null ? 0 : clamp(now - frameT, 0, 0.1);
      frameT = now;
      if (unitR > 0) r0 = unitR;
      stamp += 1;
      for (var i = shrinkSpots.length - 1; i >= 0; i--) {
        shrinkSpots[i].age += dt;
        if (shrinkSpots[i].age > 0.25) shrinkSpots.splice(i, 1);
      }
    },

    /**
     * @param {{ body: object, tier?: number }} meta
     * @param {number} r drawn radius (px)
     * @param {{
     *   merge?: { anim: object, motion: object }|null,
     *   shrinking?: boolean,
     *   pop?: number,
     *   reduced?: boolean,
     * }} [ctx]
     * @returns {BodyPose}
     */
    pose: function (meta, r, ctx) {
      var c = ctx || {};
      var body = meta.body;
      var id = body.id;
      var st = stateFor(id, body);
      st.seen = stamp;
      var t = frameT || 0;
      var m = IDENTITY;
      var dx = 0, dy = 0;
      var alpha = 1;
      var blink = isBlinking(id, t);
      var lift = false;

      if (st.fresh) {
        st.fresh = false;
        // A shrink's replacement body: boing where the old one was.
        for (var s = 0; s < shrinkSpots.length; s++) {
          var sp = shrinkSpots[s];
          var ddx = sp.x - body.position.x, ddy = sp.y - body.position.y;
          if (ddx * ddx + ddy * ddy <= sp.r * sp.r) {
            st.hitAmp = BODY_LIFE.squash * 0.9;
            st.hitAge = 0;
            st.nx = 0;
            st.ny = 1;
            shrinkSpots.splice(s, 1);
            break;
          }
        }
      }

      if (c.merge) {
        // Merging bodies are kinematic: their pose comes from the tween, not from velocity.
        var anim = c.merge.anim;
        var tm = mergeProgress(anim, body, c.merge.motion);
        var dirX = anim ? anim.mx - body.position.x : 0;
        var dirY = anim ? anim.my - body.position.y : 0;
        if (anim && tm < 0.42 && !(dirX * dirX + dirY * dirY > 0.25)) {
          var isA = anim.a === body || (anim.a && anim.a.id === id);
          dirX = anim.mx - (isA ? anim.startAx : anim.startBx);
          dirY = anim.my - (isA ? anim.startAy : anim.startBy);
        }
        var mp = mergePose(tm, dirX, dirY);
        st.pvx = 0;
        st.pvy = 0;
        return { m: mp.m, dx: 0, dy: 0, alpha: mp.alpha, blink: mp.blink, lift: true, identity: false };
      }

      if (c.shrinking) {
        shrinkSpots.push({ x: body.position.x, y: body.position.y, r: Math.max(4, r), age: 0 });
        if (shrinkSpots.length > 8) shrinkSpots.shift();
        st.pvx = 0;
        st.pvy = 0;
        return { m: IDENTITY, dx: 0, dy: 0, alpha: 1, blink: true, lift: true, identity: true };
      }

      if (c.reduced) {
        st.pvx = body.velocity ? body.velocity.x : 0;
        st.pvy = body.velocity ? body.velocity.y : 0;
        return { m: IDENTITY, dx: 0, dy: 0, alpha: 1, blink: blink, lift: false, identity: true };
      }

      trackImpact(st, body, r);
      st.hitAge += dt;

      var v = body.velocity || { x: 0, y: 0 };
      var speed = Math.sqrt(v.x * v.x + v.y * v.y);
      // Velocity stretch, eased so a contact jitter does not flicker it.
      var target = clamp((speed / r0 - BODY_LIFE.stretchFrom) * BODY_LIFE.stretchGain, 0, BODY_LIFE.stretchMax);
      var ease = clamp(dt * 18, 0, 1);
      st.stretch += (target - st.stretch) * ease;
      if (speed > 1e-3 && target > 0) {
        st.sax = v.x / speed;
        st.say = v.y / speed;
      }
      st.rest += ((speed < 0.15 * r0 ? 1 : 0) - st.rest) * clamp(dt * 4, 0, 1);

      var hit = st.hitAmp > 0 ? st.hitAmp * Math.exp(-st.hitAge * BODY_LIFE.squashDecay) : 0;
      if (hit < 0.002) st.hitAmp = 0;
      if (hit > 0.12) blink = true;

      if (st.stretch > 0.002) m = axisSquash(1 + st.stretch, st.sax, st.say);
      if (st.hitAmp > 0) m = mul(axisSquash(landingSquash(st.hitAmp, st.hitAge), st.nx, st.ny), m);
      var breathe = BODY_LIFE.breathe * st.rest;
      if (breathe > 0.001) {
        var period = 2.4 + 1.2 * hash(id * 0.53 + 1.9);
        var b = breathe * Math.sin(TAU * t / period + hash(id * 3.3) * TAU);
        m = mul([1 + b, 0, 0, 1 - b], m);
      }

      var pop = c.pop != null && isFinite(c.pop) ? c.pop : 1;
      if (Math.abs(pop - 1) > 0.002) {
        m = mul(popPose(pop), m);
        lift = true;
      }

      // Keep the contact point where it is: shift the centre so the deformed edge meets it
      // (toward what was hit while a squash runs, else down onto the floor at rest).
      var nx = 0, ny = 0;
      if (hit > 0.004) {
        nx = st.nx;
        ny = st.ny;
      } else if (st.rest > 0.01 && Math.abs(pop - 1) <= 0.002) {
        ny = st.rest;
      }
      if (nx || ny) {
        dx = r * (nx - (m[0] * nx + m[2] * ny));
        dy = r * (ny - (m[1] * nx + m[3] * ny));
      }
      return {
        m: m,
        dx: dx,
        dy: dy,
        alpha: alpha,
        blink: blink,
        lift: lift,
        identity: isIdentity(m) && Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01,
      };
    },

    end: function () {
      states.forEach(function (st, id) {
        if (st.seen !== stamp) states.delete(id);
      });
    },

    /** Test hook. */
    size: function () {
      return states.size;
    },
  };
}
